import type { ReactElement } from 'react';
import { useT } from '../lib/i18n';

/**
 * The way into search that is actually on screen.
 *
 * A component of its own, and not two buttons drawn where they are needed,
 * because there are two places — the rail on a wide screen, the top bar on a
 * phone — and a magnifying glass that sat a pixel differently in each is exactly
 * the sort of drift a shared component removes.
 *
 * **An icon rather than an inert field.** The usual pattern is a fake input
 * saying "Search…", and there is nowhere to put one: the top bar exists only
 * below `lg`, beside the brand, the language switcher and the way out, and on a
 * 375 px screen that is already the whole width. On a wide screen there is no
 * top bar at all — the rail is 68 px, which is one icon.
 *
 * The shortcut is in the `title` rather than printed beside it, for the same
 * reason: there is no room, and somebody who wants a shortcut hovers.
 */
export function SearchTrigger({
  onOpen,
  className = '',
}: {
  onOpen: () => void;
  className?: string;
}): ReactElement {
  const t = useT();

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={t('search.open')}
      title={t('search.shortcut')}
      className={`flex items-center justify-center rounded-lg text-ink-500 transition-colors hover:bg-surface hover:text-ink-900 ${className}`}
    >
      <svg
        viewBox="0 0 24 24"
        className="size-5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="11" cy="11" r="6" />
        <path d="m20 20-4.5-4.5" />
      </svg>
    </button>
  );
}
